/**
 * Generic timeline view with colored thread lanes for events.
 *
 * Top N email threads get assigned colored vertical lanes for visual tracking.
 * Other threads are grouped in a gray "other" lane. Messages are displayed
 * as bubbles with sender tabs, content previews, and attachments.
 *
 * Features:
 * - Sticky headers showing current thread names
 * - Visual lane continuity (solid/dashed lines)
 * - Search and thread filtering (optional)
 * - Attachment downloads
 * - Click handlers for emails
 *
 * @example
 * <EventsTimeline
 *   events={timelineEvents}
 *   onOpenEmail={(emailId) => openEmail(emailId)}
 *   numberOfLanes={4}
 *   laneThickness={10}
 * />
 */

import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Link,
  IconButton,
  Collapse,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";

import type { EventsTimelineProps, TimelineEvent } from "./types";
import {
  DEFAULT_LANE_COLORS,
  DEFAULT_OTHER_THREADS_LANE_COLOR,
} from "./constants";
import {
  useThreadCalculations,
  useStickyHeaders,
  useLanePositions,
} from "./hooks";
import {
  TimelineFilters,
  StickyThreadHeader,
  LaneSegments,
  TimelineMessage,
} from "./components";

export function EventsTimeline({
  events,
  loading = false,
  error = null,
  onOpenEmail,
  onOpenThread,
  onDownloadAttachment,
  numberOfLanes = 4,
  laneThickness = 10,
  laneSpacing = 40,
  lanesLeftOffset = 10,
  bubbleWidth = 50,
  bubbleBaseOffset = 0,
  bubbleReplyOffsetPercent = 0,
  dashedLineLength = 16,
  topPadding = 0,
  bottomPadding = 68,
  dateColumnWidth = 50,
  laneColors = DEFAULT_LANE_COLORS,
  otherThreadsLaneColor = DEFAULT_OTHER_THREADS_LANE_COLOR,
  showFilters = true,
  showStickyHeaders = true,
  datePosition = "left",
  filtersCollapsible = false,
  highlightedEmailId = null,
}: EventsTimelineProps) {
  // Filter state
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [showOnlyWithDocuments, setShowOnlyWithDocuments] = useState(false);
  const [selectedThreadIds, setSelectedThreadIds] = useState<string[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(!filtersCollapsible);

  // Refs for tracking
  const timelineContainerRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Filter and sort events chronologically
  const filteredAndSortedEvents = useMemo(() => {
    let filtered = events;

    // Get thread info for filtering
    const threadMap = new Map<string, { count: number }>();
    events.forEach((event) => {
      if (event.threadId) {
        const existing = threadMap.get(event.threadId);
        if (existing) {
          existing.count++;
        } else {
          threadMap.set(event.threadId, { count: 1 });
        }
      }
    });
    const totalThreads = threadMap.size;

    // Apply thread filter (only skip filtering when ALL threads are selected)
    if (totalThreads > 0 && selectedThreadIds.length < totalThreads) {
      filtered = filtered.filter(
        (event) => event.threadId && selectedThreadIds.includes(event.threadId),
      );
    }

    // Apply document filter
    if (showOnlyWithDocuments) {
      filtered = filtered.filter(
        (event) => event.attachments && event.attachments.length > 0,
      );
    }

    // Apply search query filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();

      filtered = filtered.filter((event) => {
        if (event.name?.toLowerCase().includes(query)) return true;
        if (event.emailSubject?.toLowerCase().includes(query)) return true;
        if (event.snippet?.toLowerCase().includes(query)) return true;
        if (event.preview?.toLowerCase().includes(query)) return true;
        if (
          event.categories
            ?.join(" ")
            .replace(/_/g, " ")
            .toLowerCase()
            .includes(query)
        )
          return true;
        if (
          event.attachments?.some((att) =>
            att.description?.toLowerCase().includes(query),
          )
        )
          return true;
        return false;
      });
    }

    // Sort by date (oldest first, so most recent appears at bottom like iMessage)
    filtered.sort((a, b) => {
      if (a.date && b.date) {
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      }
      return 0;
    });

    return filtered;
  }, [events, searchQuery, showOnlyWithDocuments, selectedThreadIds]);

  // Thread calculations
  const {
    threadInfo,
    laneThreads,
    getLaneInfo,
    threadMessageIndices,
    otherThreadSegments,
  } = useThreadCalculations(
    events,
    filteredAndSortedEvents,
    numberOfLanes,
    laneColors,
  );

  // Lane positions
  const { messageLanePositions, updateLanePositions } = useLanePositions({
    messagesContainerRef,
    messageRefs,
    events: filteredAndSortedEvents,
  });

  // Sticky headers
  const { stickyLaneThreads, stickyOtherThreadSegment } = useStickyHeaders({
    containerRef: timelineContainerRef,
    messageRefs,
    laneThreads,
    threadMessageIndices,
    otherThreadSegments,
    updateLanePositions,
  });

  // Initialize selected threads when events load
  useEffect(() => {
    if (threadInfo.length > 0 && selectedThreadIds.length === 0) {
      setSelectedThreadIds(threadInfo.map((t) => t.threadId));
    }
  }, [threadInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to bottom (most recent messages) when events load - like iMessage
  useEffect(() => {
    if (filteredAndSortedEvents.length > 0 && timelineContainerRef.current) {
      // Small delay to ensure DOM is rendered
      setTimeout(() => {
        if (timelineContainerRef.current) {
          timelineContainerRef.current.scrollTop =
            timelineContainerRef.current.scrollHeight;
        }
      }, 150);
    }
  }, [filteredAndSortedEvents.length]);

  // Scroll to highlighted email when highlightedEmailId changes
  useEffect(() => {
    if (!highlightedEmailId || !timelineContainerRef.current) return;

    const msgIndex = filteredAndSortedEvents.findIndex(
      (e) => e.emailId === highlightedEmailId,
    );
    if (msgIndex < 0) return;

    const el = messageRefs.current.get(`msg-${msgIndex}`);
    if (el && timelineContainerRef.current) {
      setTimeout(() => {
        const container = timelineContainerRef.current;
        if (!container) return;
        // Account for the sticky header height
        let stickyOffset = 0;
        for (const child of Array.from(container.children)) {
          const cs = getComputedStyle(child);
          if (cs.position === "sticky") {
            stickyOffset = (child as HTMLElement).getBoundingClientRect().height;
            break;
          }
        }
        const elRect = el.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const offsetTop = elRect.top - containerRect.top + container.scrollTop;
        container.scrollTo({ top: offsetTop - stickyOffset - 12, behavior: "smooth" });
      }, 100);
    }
  }, [highlightedEmailId, filteredAndSortedEvents]);

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // Empty state
  if (events.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No events found
        </Typography>
      </Box>
    );
  }

  // Event handlers
  const handleOpenEmail = (eventEmailId: string) => {
    onOpenEmail?.(eventEmailId);
  };

  const handleDownloadAttachment = async (
    emailId: string,
    attachmentId: string,
    filename?: string,
  ) => {
    onDownloadAttachment?.(emailId, attachmentId, filename);
  };

  // Gray lane X position
  const grayLaneX =
    lanesLeftOffset + laneThreads.length * (laneThickness + laneSpacing);

  return (
    <Box
      className="EventsTimeline"
      sx={{
        pl: 0,
        pt: 0,
        pb: 0,
        pr: 0,
        flex: 1,
        minHeight: 0,
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Filters Row */}
      {showFilters && events.length > 0 && (
        <Box sx={{ flexShrink: 0, position: "relative" }}>
          {/* Toggle button for collapsible mode */}
          {filtersCollapsible && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", pr: 1, pt: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => setFiltersOpen((prev) => !prev)}
                sx={{
                  color: filtersOpen ? "primary.main" : "text.secondary",
                  fontSize: "0.75rem",
                }}
              >
                {filtersOpen ? (
                  <FilterListOffIcon sx={{ fontSize: 20 }} />
                ) : (
                  <FilterListIcon sx={{ fontSize: 20 }} />
                )}
              </IconButton>
            </Box>
          )}
          <Collapse in={filtersOpen}>
            <Box
              sx={{
                pt: filtersCollapsible ? 0 : 1,
                pr: 2,
                ...(filtersCollapsible
                  ? {
                      position: "absolute",
                      top: 32,
                      left: 0,
                      right: 0,
                      zIndex: 20,
                      bgcolor: "background.paper",
                      pb: 1,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    }
                  : {}),
              }}
            >
              <TimelineFilters
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                showOnlyWithDocuments={showOnlyWithDocuments}
                onShowOnlyWithDocumentsChange={setShowOnlyWithDocuments}
                threadInfo={threadInfo}
                selectedThreadIds={selectedThreadIds}
                onThreadFilterChange={setSelectedThreadIds}
              />
            </Box>
          </Collapse>
        </Box>
      )}

      {/* No filtered results */}
      {events.length > 0 &&
        searchQuery.trim() &&
        filteredAndSortedEvents.length === 0 && (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No messages match your search
            </Typography>
          </Box>
        )}

      {/* Timeline with Thread Lines */}
      {events.length > 0 &&
        (!searchQuery.trim() || filteredAndSortedEvents.length > 0) && (
          <Box
            className="timeline-container"
            ref={timelineContainerRef}
            sx={{
              position: "relative",
              flex: 1,
              minHeight: 0,
              overflow: "auto",
            }}
          >
            {/* Sticky Header container */}
            {showStickyHeaders && (
              <Box
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                  minHeight: 0,
                }}
              >
                <StickyThreadHeader
                  stickyLaneThreads={stickyLaneThreads}
                  stickyOtherThreadSegment={stickyOtherThreadSegment}
                  laneThreads={laneThreads}
                  otherThreadSegments={otherThreadSegments}
                  laneThickness={laneThickness}
                  laneSpacing={laneSpacing}
                  lanesLeftOffset={lanesLeftOffset}
                  otherThreadsLaneColor={otherThreadsLaneColor}
                  dateColumnWidth={dateColumnWidth}
                />
              </Box>
            )}

            {/* Content area with lanes and messages */}
            <Box
              className="messages-and-lanes-container"
              sx={{
                position: "relative",
                zIndex: 0,
                minHeight: 200,
                pt: `${topPadding}px`,
                pb: `${bottomPadding}px`,
              }}
            >
              {/* Lane segments - higher z-index to appear above date separator lines */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 2,
                  pointerEvents: "none",
                }}
              >
                <LaneSegments
                  events={filteredAndSortedEvents}
                  messageLanePositions={messageLanePositions}
                  getLaneInfo={getLaneInfo}
                  threadMessageIndices={threadMessageIndices}
                  stickyLaneThreads={stickyLaneThreads}
                  laneThickness={laneThickness}
                  laneSpacing={laneSpacing}
                  lanesLeftOffset={lanesLeftOffset}
                  dashedLineLength={dashedLineLength}
                  otherThreadsLaneColor={otherThreadsLaneColor}
                  laneThreadsCount={laneThreads.length}
                  dateColumnWidth={dateColumnWidth}
                />
              </Box>

              {/* Messages */}
              <Box
                ref={messagesContainerRef}
                sx={{
                  position: "relative",
                }}
              >
                {filteredAndSortedEvents.map((event, index) => {
                  const prevEvent =
                    index > 0 ? filteredAndSortedEvents[index - 1] : null;

                  const showSubject =
                    !prevEvent || prevEvent.threadId !== event.threadId;

                  const currentSender = event.emailFrom?.[0] || "";
                  const prevSender = prevEvent?.emailFrom?.[0] || "";
                  const showSender =
                    currentSender !== prevSender || showSubject;

                  const isSameSenderAsPrevious =
                    currentSender === prevSender && !showSubject;

                  const currentDate = event.date
                    ? new Date(event.date).toDateString()
                    : "";
                  const prevDate = prevEvent?.date
                    ? new Date(prevEvent.date).toDateString()
                    : "";
                  const showDateBullet = currentDate !== prevDate;

                  const laneInfo = getLaneInfo(event.threadId);

                  const threadIndices = event.threadId
                    ? threadMessageIndices.get(event.threadId)
                    : undefined;
                  const isFirstMessageOfLaneThread =
                    threadIndices?.first === index;
                  const isThreadSticky = event.threadId
                    ? stickyLaneThreads.has(event.threadId)
                    : false;
                  const showLaneSubjectInline = !!(
                    laneInfo && isFirstMessageOfLaneThread
                  );
                  const isLaneSubjectHidden = isThreadSticky;

                  // Compute lane info for unified TimelineMessage component
                  const isFirstOfThreadChunk = showSubject && !!prevEvent;
                  let lane: { color: string; xPosition: number };
                  let showSubjectInline: boolean;
                  let isSubjectHidden: boolean;

                  if (laneInfo) {
                    // Colored lane thread
                    lane = {
                      color: laneInfo.color,
                      xPosition:
                        lanesLeftOffset +
                        laneInfo.laneIndex * (laneThickness + laneSpacing),
                    };
                    showSubjectInline = showLaneSubjectInline;
                    isSubjectHidden = isLaneSubjectHidden;
                  } else {
                    // Gray "other threads" lane
                    const segmentIndex = otherThreadSegments.findIndex(
                      (seg) =>
                        seg.firstIndex <= index && index <= seg.lastIndex,
                    );
                    const segment =
                      segmentIndex >= 0
                        ? otherThreadSegments[segmentIndex]
                        : null;
                    const isFirstOfSegment = segment
                      ? segment.firstIndex === index
                      : false;
                    const isSegmentSticky =
                      stickyOtherThreadSegment === segmentIndex;

                    lane = {
                      color: otherThreadsLaneColor,
                      xPosition: grayLaneX,
                    };
                    showSubjectInline = isFirstOfSegment;
                    isSubjectHidden = isSegmentSticky;
                  }

                  return (
                    <TimelineMessage
                      key={event.emailId || `msg-${index}`}
                      event={event}
                      lane={lane}
                      showSubjectInline={showSubjectInline}
                      isSubjectHidden={isSubjectHidden}
                      showSender={showSender}
                      showDateBullet={showDateBullet}
                      isSameSenderAsPrevious={isSameSenderAsPrevious}
                      isFirstOfThreadChunk={isFirstOfThreadChunk}
                      onOpenEmail={handleOpenEmail}
                      onDownloadAttachment={handleDownloadAttachment}
                      laneThickness={laneThickness}
                      bubbleWidth={bubbleWidth}
                      bubbleBaseOffset={bubbleBaseOffset}
                      bubbleReplyOffsetPercent={bubbleReplyOffsetPercent}
                      dateColumnWidth={dateColumnWidth}
                      datePosition={datePosition}
                      refCallback={(el) => {
                        if (el) {
                          messageRefs.current.set(`msg-${index}`, el);
                        }
                      }}
                      isHighlighted={event.emailId === highlightedEmailId}
                    />
                  );
                })}
              </Box>

              {/* End of Timeline Footer */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  pt: 4,
                  pb: 10,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.75rem",
                    textAlign: "center",
                  }}
                >
                  End of email exchanges, total {filteredAndSortedEvents.length}{" "}
                  emails.
                  <br />
                  Scroll up for earlier messages, or{" "}
                  <Link
                    component="span"
                    onClick={() => {
                      if (timelineContainerRef.current) {
                        timelineContainerRef.current.scrollTo({
                          top: 0,
                          behavior: "smooth",
                        });
                      }
                    }}
                    sx={{
                      cursor: "pointer",
                      color: "primary.main",
                      textDecoration: "underline",
                    }}
                  >
                    jump to top
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
    </Box>
  );
}
